import customerOwnedAwsService from '@/services/registercustomerowned.service.js'
import oppRegService from '@/services/oppreg.service.js'
import { TokenService } from '@/services/storage.service'


export default {
  submitForm: async (context, formData) => {
    context.commit('clearFormErrors')
    context.commit('setIsSubmitting', true)
    console.log('Form data before validation:', formData) // Debug log to check form data before validation
    try {
      // Validate form
      const errors = validateForm(formData)
      if (!errors.opportunityId && !context.state.oppIdValidated) {
        errors.opportunityId = 'Opportunity ID not yet registered. Go to Galaxy "Opportunity Registrations" to register.'
      }
      if (Object.keys(errors).length > 0) {
        context.commit('setFormErrors', errors)
        context.commit('setIsSubmitting', false)
        return
      }
      console.log('Form data is valid, proceeding to submit to API')
      formData.action = 'create'
      console.log('Form data to be submitted:', formData) // Debug log to check form data before API submission
      const currentUser = TokenService.getCurrentUser() || {}
      const payload = {
        ...formData,
        requestorId: currentUser.requestor_id || null,
        requestorEmail: currentUser.email || null,
        awsAccountIds: formData.awsAccountIds.length > 0 ? JSON.stringify(formData.awsAccountIds) : ''
      }
      if (formData.requestManagedServices === false) {
        payload.status = 'Completed'
      } else {
        payload.status = 'In Progress'
      }
      console.log('Payload to be submitted to API:', payload)

      // Submit to API
      await customerOwnedAwsService.submitCustomerAccount(payload)

      // Send email notification after successful submission
      const listAccounts = formData.awsAccountIds.map(account => account.accountId).join(', ')
      const emailData = {
        template: 'managed-svcs-new',
        sender: 'AWS-Galaxy@cscportal.onmicrosoft.com',
        from_alias: 'Galaxy DXC AWS Practice',
        send_to: currentUser.email,
        opportunity_id: formData.opportunityId,
        aws_account_id: listAccounts
      }
      await context.dispatch('sendEmailNotification', emailData)
      const onboardingTeamEmailData = {
        template: 'managed-svcs-to-onboarding-team',
        opportunity_id: formData.opportunityId,
        aws_account_id: listAccounts
      }
      await context.dispatch('sendEmailNotification', onboardingTeamEmailData)

      context.commit('setSubmitSuccess', true)
      context.commit('resetForm')
      context.commit('setIsSubmitting', false)
    } catch (error) {
      console.log('Error submitting customer account:', error)
      console.error('Error submitting customer account:', error)
      context.commit('setSubmitError', error.message)
      context.commit('setIsSubmitting', false)
    }
  },
  fetchRequests: async (context, data) => {
    context.commit('setIsLoading', true)
    try {
      let parameter = {}
      if (data && data.nextPageKey) {
        parameter.nextPageKey = data.nextPageKey
      } else {
        context.commit('clearRequests')
      }
      const requests = await customerOwnedAwsService.getCustomerOwnedAccounts(parameter)
      context.commit('setRequests', requests.data)
      context.commit('setNextPageKey', requests.next_data || null)
    } catch (error) {
      console.error('Error fetching customer accounts:', error)
      context.commit('setSubmitError', error.message)
    } finally {
      context.commit('setIsLoading', false)
    }
  },
  validateOppId: async (context, opportunityId) => {
    console.log('Validating Opportunity ID:', opportunityId)
    context.commit('setIsValidatingOppId', true)
    try {
      const opportunity = await oppRegService.findOpportunityById(opportunityId)
      console.log('Opportunity validation response:', opportunity)
      const isValidOpportunity = opportunity && Object.keys(opportunity).length > 0
      console.log('isValidOpportunity', isValidOpportunity)
      context.commit('setOppIdValidated', isValidOpportunity)
      context.commit('setOppIdData', opportunity || null)
      context.commit('setCustomerName', opportunity.Customer_Name || '')
      if (isValidOpportunity) {
        context.commit('setFormErrorField', { field: 'opportunityId', message: null })
      } else {
        context.commit('setFormErrorField', {
          field: 'opportunityId',
          message: 'Opportunity ID is invalid. Please check the ID and try again.'
        })
      }
    } catch (error) {
      context.commit('setOppIdValidated', false)
      context.commit('setFormErrorField', {
        field: 'opportunityId',
        message: 'Opportunity ID is invalid. Please check the ID and try again.'
      })
    } finally {
      context.commit('setIsValidatingOppId', false)
    }
  },
  downloadTemplate: async (context) => {
    try {
      const response = await customerOwnedAwsService.downloadTemplate()
      window.open(response.data, '_blank').focus()
      return response
    } catch (error) {
      console.error('Error downloading customer-owned template:', error)
      context.commit('setSubmitError', error.message)
      throw error
    }
  },
  sendEmailNotification: async (context, emailData) => {
    try {
      console.log('Sending email notification with data:', emailData)
      const response = await customerOwnedAwsService.sendEmailNotification(emailData)
      console.log('Email notification sent successfully:', response.data)
      return response.data
    } catch (error) {
      console.error('Error sending email notification:', error)
      context.commit('setSubmitError', error.message)
      throw error
    }
  }
}

function validateForm (formData) {
  const errors = {}

  // Phase 1 validation
  if (!formData.customerName || formData.customerName.trim() === '') {
    errors.customerName = 'Customer Name is required'
  }
  if (!formData.opportunityId || formData.opportunityId.trim() === '') {
    errors.opportunityId = 'Opportunity ID is required'
  }
  if (!formData.awsAccountIds || formData.awsAccountIds.length === 0) {
    errors.awsAccountIds = 'AWS Account ID(s) is required'
  }
  if (!formData.supportingPdl || formData.supportingPdl.trim() === '') {
    errors.supportingPdl = 'Supporting PDL is required'
  } else if (!/\S+@\S+\.\S+/.test(formData.supportingPdl)) {
    errors.supportingPdl = 'Supporting PDL must be a valid email address'
  }
  if (!formData.contactName || formData.contactName.trim() === '') {
    errors.contactName = 'Contact Name is required'
  }
  if (!formData.contactEmail || formData.contactEmail.trim() === '') {
    errors.contactEmail = 'Contact Email is required'
  } else if (!/\S+@\S+\.\S+/.test(formData.contactEmail)) {
    errors.contactEmail = 'Contact Email must be a valid email address'
  }

  // Phase 2 validation (if managed services requested)
  if (formData.requestManagedServices) {
    if (!formData.wbsCode || formData.wbsCode.trim() === '') {
      errors.wbsCode = 'WBS Code is required when requesting managed services'
    }
    if (!formData.targetDeploymentDate) {
      errors.targetDeploymentDate = 'Target Deployment Date is required when requesting managed services'
    }
  }
  console.log('Validation errors:', errors) // Debug log to check validation errors
  return errors
}
